import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useState, useRef } from 'react';
import { TableRow, SkeletonTableRow } from './TableRow';
import { hasValue } from '@utils';
import { Dropdown } from '@widget/popups/Dropdown';
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import ActionsListComponent from "./ActionListComponent";
import IconButton from '@widget/buttons/IconButton';
import Checkbox from '@widget/forms/inputs/Checkbox';
/**
 * @export
 * Width reserved for the editable actions column (Save/Cancel/Delete buttons)
 */
const ACTION_COLUMN_WIDTH = 300;
const RowActionsDropdown = ({ actions, item }) => {
    const rowDropdownRef = useRef(null);
    const resolved = typeof actions === 'function' ? actions(item) : actions;
    if (Array.isArray(resolved)) {
        const visible = resolved.filter(a => !a.visible || a.visible(item));
        if (visible.length === 0)
            return null;
    }
    else if (!resolved) {
        return null;
    }
    return (_jsx(Dropdown, { ref: rowDropdownRef, trigger: _jsx("div", { className: "uxpcore_table__actions-actionbtn", children: _jsx(FontAwesomeIconWrapper, { icon: ["fal", "ellipsis-h"] }) }), content: _jsx(ActionsListComponent, { actions: actions, item: item, parentDropdownRef: rowDropdownRef }) }));
};
export const processColumns = (params) => {
    var _a, _b;
    const { columns, defaultColumns, containerWidth, minCellWidth, columnsToHide, selectedCount, totalCount, resizedColumns, editableMode } = params;
    if (!containerWidth)
        return null;
    const { editColumn, deleteColumn, selectColumn, expandColumn, actionsColumn, dragHandleColumn } = defaultColumns;
    const reservedSpace = 10
        + ((editColumn === null || editColumn === void 0 ? void 0 : editColumn.enable) ? (editColumn === null || editColumn === void 0 ? void 0 : editColumn.width) || 60 : 0)
        + ((deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.enable) ? (deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.width) || 60 : 0)
        + ((selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.enable) ? (selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.width) || 40 : 0)
        + ((expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.enable) ? (expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.width) || 40 : 0)
        + ((actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.enable) ? (actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.width) || 40 : 0)
        + ((dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.enable) ? (dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.width) || 40 : 0)
        + (editableMode ? ACTION_COLUMN_WIDTH : 0);
    let availableSpace = containerWidth - reservedSpace;
    const filteredColumns = (!!columnsToHide) ? columns.filter(c => !columnsToHide.includes(c.id)) : columns;
    const columnsWithMinWidth = filteredColumns.filter(column => hasValue(column.minWidth));
    const columnsWithoutMinWidth = filteredColumns.filter(column => !hasValue(column.minWidth));
    const allocatedWidth = columnsWithMinWidth.reduce((sum, column) => sum + (column.minWidth || 0), 0);
    availableSpace -= allocatedWidth;
    if (availableSpace < 0)
        availableSpace = 0;
    const remainingColumnWidth = Math.max((availableSpace / (columnsWithoutMinWidth.length || 1)), minCellWidth);
    const additionalSpace = (columnsWithoutMinWidth.length == 0) ? (availableSpace / columnsWithMinWidth.length || 1) : 0;
    const processedColumns = {};
    if (dragHandleColumn === null || dragHandleColumn === void 0 ? void 0 : dragHandleColumn.enable) {
        processedColumns['__dragHandleColumn__'] = {
            id: '__dragHandleColumn__',
            label: dragHandleColumn.label || '',
            minWidth: dragHandleColumn.width,
            maxWidth: dragHandleColumn.width,
            width: dragHandleColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (expandColumn === null || expandColumn === void 0 ? void 0 : expandColumn.enable) {
        processedColumns['__expandColumn__'] = {
            id: '__expandColumn__',
            label: expandColumn.label || '',
            minWidth: expandColumn.width,
            maxWidth: expandColumn.width,
            width: expandColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.enable) {
        processedColumns["__actionsColumn__"] = {
            id: "__actionsColumn__",
            label: (_jsx(Dropdown, { trigger: _jsx("div", { className: "uxpcore_table__actions-label", children: _jsx(FontAwesomeIconWrapper, { icon: ["fal", "sliders-h"] }) }), content: _jsx(ActionsListComponent, { actions: (_a = actionsColumn.headerActions) !== null && _a !== void 0 ? _a : [] }), preventOpening: ((_b = actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.headerActions) === null || _b === void 0 ? void 0 : _b.length) == 0, ref: actionsColumn === null || actionsColumn === void 0 ? void 0 : actionsColumn.ref })),
            renderColumn: (item) => {
                var _a;
                return (_jsx(RowActionsDropdown, { actions: (_a = actionsColumn.rowActions) !== null && _a !== void 0 ? _a : [], item: item }));
            },
            minWidth: actionsColumn.width,
            maxWidth: actionsColumn.width,
            width: actionsColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.enable) {
        processedColumns['__selectColumn__'] = {
            id: '__selectColumn__',
            label: _jsx(Checkbox, { checked: selectedCount > 0 && totalCount > 0 ? (selectedCount == totalCount ? true : 'intermediate') : false, onChange: (v) => { var _a; return (_a = selectColumn === null || selectColumn === void 0 ? void 0 : selectColumn.onSelectAll) === null || _a === void 0 ? void 0 : _a.call(selectColumn, v); }, type: 'bordered', className: 'uxpcore_table__select-checkbox' }),
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
            minWidth: selectColumn.width,
            maxWidth: selectColumn.width,
            width: selectColumn.width,
            isResizable: false,
        };
    }
    filteredColumns.forEach(column => {
        const minW = hasValue(column.minWidth) ? (column.minWidth + additionalSpace) : remainingColumnWidth;
        let width = hasValue(resizedColumns === null || resizedColumns === void 0 ? void 0 : resizedColumns[column.id]) ? resizedColumns[column.id] : minW;
        // Enforce maxWidth constraint
        if (hasValue(column.maxWidth) && width > column.maxWidth) {
            width = column.maxWidth;
        }
        processedColumns[column.id] = Object.assign(Object.assign({}, column), { width: width, minWidth: minW, maxWidth: column.maxWidth, isResizable: (column === null || column === void 0 ? void 0 : column.isResizable) || false, isSortable: (column === null || column === void 0 ? void 0 : column.isSortable) || false });
    });
    if (editColumn === null || editColumn === void 0 ? void 0 : editColumn.enable) {
        processedColumns['__editColumn__'] = {
            id: '__editColumn__',
            label: editColumn.label || 'Edit',
            renderColumn: editColumn.renderColumn || ((item) => {
                // Check visibility
                if (editColumn.visible && !editColumn.visible(item)) {
                    return null;
                }
                return _jsx(IconButton, { type: 'edit', onClick: () => { var _a; return (_a = editColumn.onEdit) === null || _a === void 0 ? void 0 : _a.call(editColumn, item); }, variant: 'secondary' });
            }),
            minWidth: editColumn.width,
            maxWidth: editColumn.width,
            width: editColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (deleteColumn === null || deleteColumn === void 0 ? void 0 : deleteColumn.enable) {
        processedColumns['__deleteColumn__'] = {
            id: '__deleteColumn__',
            label: deleteColumn.label || 'Delete',
            renderColumn: deleteColumn.renderColumn || ((item) => {
                // Check visibility
                if (deleteColumn.visible && !deleteColumn.visible(item)) {
                    return null;
                }
                return _jsx(IconButton, { type: 'delete', onClick: () => { var _a; return (_a = deleteColumn.onDelete) === null || _a === void 0 ? void 0 : _a.call(deleteColumn, item); }, variant: 'danger' });
            }),
            minWidth: deleteColumn.width,
            maxWidth: deleteColumn.width,
            width: deleteColumn.width,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    if (editableMode) {
        processedColumns['__editActionsColumn__'] = {
            id: '__editActionsColumn__',
            label: ' ',
            minWidth: ACTION_COLUMN_WIDTH,
            maxWidth: ACTION_COLUMN_WIDTH,
            width: ACTION_COLUMN_WIDTH,
            isResizable: false,
            onClickColumn(e, item, column) {
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }
    return processedColumns;
};
const TableResizeHandler = memo(({ onResize }) => {
    const handleMouseDown = useCallback((e) => {
        var _a;
        const startX = e.clientX;
        const startWidth = ((_a = e.target.parentElement) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const handleMouseMove = (e) => {
            const diff = e.clientX - startX;
            onResize(startWidth + diff);
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [onResize]);
    return (_jsx("div", { className: "uxpcore_table__resize-handle", onMouseDown: handleMouseDown }));
});
/**
 * @export
 * Removes columns with no sort order (0) from the sort configuration
 *
 * sort - The sort configuration to clean
 * returns Cleaned sort configuration with only sorted columns
 *
 * @example
 * ```tsx
 * const cleaned = cleanSort({ name: 1, age: 0, status: -1 });
 * // Result: { name: 1, status: -1 }
 * ```
 */
export function cleanSort(sort) {
    return Object.fromEntries(Object.entries(sort || {}).filter(([_, order]) => order !== 0));
}
/**
 * @export
 * Checks if any columns are currently sorted
 *
 * sort - The sort configuration to check
 * returns True if at least one column has a sort order
 *
 * @example
 * ```tsx
 * hasSort({ name: 1, age: 0 }) // true
 * hasSort({ name: 0, age: 0 }) // false
 * ```
 */
export function hasSort(sort) {
    const cleaned = cleanSort(sort);
    return !!Object.keys(cleaned).length;
}
/**
 * @export
 * Returns the number of columns currently sorted
 *
 * sort - The sort configuration
 * returns Count of sorted columns
 *
 * @example
 * ```tsx
 * getSortCount({ name: 1, age: -1, status: 0 }) // 2
 * ```
 */
export function getSortCount(sort) {
    const cleaned = cleanSort(sort);
    return Object.keys(cleaned || {}).length;
}
const TableSortHandler = memo(({ isSortable, sortOrder, onChange, children }) => {
    if (!isSortable)
        return children;
    return _jsxs("div", { className: "uxpcore_table__sort-handle", onClick: () => {
            onChange(sortOrder == 0 ? 1 : sortOrder == 1 ? -1 : 0);
        }, children: [_jsx("div", { className: "uxpcore_table__sort-handle-content", children: children }), sortOrder !== 0 &&
                _jsx("div", { className: "uxpcore_table__sort-handle-indicator", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', sortOrder == 1 ? 'caret-up' : 'caret-down'] }) })] });
});
/**
 * @export
 * Core table component that displays tabular data with rich features including sorting, resizing,
 * selection, expansion, and inline editing capabilities.
 *
 * Features:
 * - Column sorting (single and multi-column)
 * - Column resizing (resets on refresh)
 * - Row selection
 * - Expandable child rows
 * - Inline editing mode
 * - Custom renderers for cells, rows, and headers
 * - Loading states with skeleton rows
 * - Action columns (edit, delete, custom actions)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => console.log('Resized', id, width)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With sorting
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   sort={{ name: 1, age: -1 }}
 *   multiColumnSort={true}
 *   onChangeSort={(newSort) => setSort(newSort)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With editable mode
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   editable={{
 *     onAddItem: async (index, item) => {
 *       await api.create(item);
 *       return true;
 *     },
 *     onUpdateItem: async (index, item) => {
 *       await api.update(item);
 *       return true;
 *     },
 *     onDeleteItem: async (index, item) => {
 *       await api.delete(item);
 *       return true;
 *     },
 *     onCancelNewItem: () => console.log('Cancelled new item'),
 *     onCancelItem: () => console.log('Cancelled edit')
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With selection and click handlers
 * <BaseTableComponent
 *   columns={processedColumns}
 *   data={users}
 *   loading={false}
 *   totalWidth="100%"
 *   handleResize={(id, width) => {}}
 *   onClickRow={(e, row) => console.log('Clicked', row)}
 *   isRowSelected={(row) => selectedIds.includes(row.id)}
 *   onSelectRow={(row) => toggleSelection(row.id)}
 * />
 * ```
 */
const BaseTableComponent = ({ columns, data, loading, noItemsMessage = 'No items found', totalWidth, renderRow, renderChildRows, renderCell, onClickRow, isRowSelected, onSelectRow, renderHeader, handleResize, sort, multiColumnSort, onChangeSort, editable }) => {
    const [internalData, setInternalData] = useState(data);
    useEffect(() => {
        setInternalData(data);
    }, [data]);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleDragEnd = (event) => {
        var _a;
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = internalData.findIndex((_, idx) => `row-${idx}` === active.id);
        const newIndex = internalData.findIndex((_, idx) => `row-${idx}` === over.id);
        const newData = arrayMove(internalData, oldIndex, newIndex);
        setInternalData(newData); // temporary update state
        (_a = editable === null || editable === void 0 ? void 0 : editable.onReorderItems) === null || _a === void 0 ? void 0 : _a.call(editable, newData);
    };
    const handleSort = (columnId, sortOrder) => {
        onChangeSort(multiColumnSort ? Object.assign(Object.assign({}, sort), { [columnId]: sortOrder }) : { [columnId]: sortOrder });
    };
    const defaultRenderHeader = () => (_jsx(_Fragment, { children: Object.values(columns).map((column) => (_jsxs("div", { className: `uxpcore_table__header-cell ${column.verticalLabel ? 'uxpcore_table__header-cell--vertical' : ''}`, style: {
                minWidth: column.minWidth,
                width: column.width,
                maxWidth: column.maxWidth
            }, children: [_jsx(TableSortHandler, { isSortable: (column === null || column === void 0 ? void 0 : column.isSortable) || false, sortOrder: (sort === null || sort === void 0 ? void 0 : sort[column.id]) || 0, onChange: (so) => { handleSort(column.id, so); }, children: _jsx("div", { className: "uxpcore_table__header-celllabel", children: column.label }) }), column.isResizable
                    ? _jsx(TableResizeHandler, { onResize: (width) => handleResize(column.id, width) })
                    : null] }, column.id))) }));
    const tableBody = (_jsx(_Fragment, { children: loading
            ? (Array.from({ length: 5 }).map((_, index) => (_jsx(SkeletonTableRow, { columns: columns }, index))))
            : (internalData === null || internalData === void 0 ? void 0 : internalData.length) === 0
                ? _jsx("div", { className: 'uxpcore_table__noitems', children: noItemsMessage })
                : (internalData.map((row, index) => {
                    var _a, _b, _c, _d, _e, _f;
                    return (renderRow
                        ? renderRow({
                            row,
                            columns,
                            onClickRow,
                            isRowSelected,
                            onSelectRow,
                            renderChildRows,
                            renderCell,
                            editable: ((editable === null || editable === void 0 ? void 0 : editable.onAddItem) || (editable === null || editable === void 0 ? void 0 : editable.onUpdateItem) || (editable === null || editable === void 0 ? void 0 : editable.onDeleteItem)) ? {
                                onAddRow: (_a = editable.onAddItem) === null || _a === void 0 ? void 0 : _a.bind(null, index),
                                onUpdateRow: (_b = editable.onUpdateItem) === null || _b === void 0 ? void 0 : _b.bind(null, index),
                                onDeleteRow: (_c = editable.onDeleteItem) === null || _c === void 0 ? void 0 : _c.bind(null, index),
                                onCancelNewRow: editable.onCancelNewItem,
                                onCancelEditing: editable.onCancelItem,
                                labels: editable.labels
                            } : undefined,
                            sortable: !!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems),
                            dragId: `row-${index}`
                        })
                        : _jsx(TableRow, { row: row, columns: columns, onClickRow: onClickRow, isRowSelected: isRowSelected, onSelectRow: onSelectRow, renderChildRows: renderChildRows, renderCell: renderCell, editable: ((editable === null || editable === void 0 ? void 0 : editable.onAddItem) || (editable === null || editable === void 0 ? void 0 : editable.onUpdateItem) || (editable === null || editable === void 0 ? void 0 : editable.onDeleteItem)) ? {
                                onAddRow: (_d = editable.onAddItem) === null || _d === void 0 ? void 0 : _d.bind(null, index),
                                onUpdateRow: (_e = editable.onUpdateItem) === null || _e === void 0 ? void 0 : _e.bind(null, index),
                                onDeleteRow: (_f = editable.onDeleteItem) === null || _f === void 0 ? void 0 : _f.bind(null, index),
                                onCancelNewRow: editable.onCancelNewItem,
                                onCancelEditing: editable.onCancelItem,
                                labels: editable.labels
                            } : undefined, sortable: !!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems), dragId: `row-${index}` }, index + (editable && row.__newitem__ ? ':__newitem__' : '')));
                })) }));
    return (_jsx("div", { className: "uxpcore_table__container", children: _jsxs("div", { className: "uxpcore_table__table", style: { minWidth: totalWidth }, children: [_jsx("div", { className: "uxpcore_table__header", children: renderHeader ? renderHeader(columns, handleResize) : defaultRenderHeader() }), _jsx("div", { className: "uxpcore_table__body", children: !!(editable === null || editable === void 0 ? void 0 : editable.onReorderItems) && !loading && (internalData === null || internalData === void 0 ? void 0 : internalData.length) > 0 ? (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: internalData.map((_, i) => `row-${i}`), strategy: verticalListSortingStrategy, children: tableBody }) })) : tableBody })] }) }));
};
export default memo(BaseTableComponent);
